/**
 * UserTier Model
 * Manages user tier definitions, limits, and feature access control
 */
import game from '../games';

export const TIERS = {
  FREE: 'free',
  PREMIUM: 'premium'
};

export const TIER_LIMITS = {
  [TIERS.FREE]: {
    // Scenario limits
    maxScenariosPerPickMode: 3, // Max 3 scenarios per 1st Pick OR 2nd Pick

    // Match pull limits
    matchPullsPerDay: 3, // Max 3 match pulls per day
    matchPullCountsTowardScenarios: false, // Match pulls don't count toward scenario limit
    maxTotalBoards: 6, // Max 6 boards total (3 user-created + 3 from match pulls)

    // Feature access
    canSeeCopyFromPickMode: false, // Hide "Copy From 1st/2nd Pick" button
    canSeePracticeMatchup: false, // Hide "Practice Match-up" feature
    canDuplicate: true, // Duplicate button VISIBLE for free tier (per scenario)
    canSeeSorting: false, // Hide drag handles for sorting scenarios
    canSeeDuplicateToggle: false, // Hide Duplicate toggle at top
    canSeeHighlightsToggle: false, // Hide Highlights toggle at top
    canSeeShareDraft: false, // Hide "Share Draft" button
    canSeeSeeMatchups: false, // Hide "See Matchups" button
    canSeeCopyToPickMode: false, // Hide "Copy to 2nd Pick" button per scenario
    canSeeViewRecords: false, // Hide "View Records" button (Firebase-saved drafts only)

    // Label
    label: 'Free',
    color: '#9e9e9e'
  },

  [TIERS.PREMIUM]: {
    // Scenario limits
    maxScenariosPerPickMode: null, // Unlimited scenarios

    // Match pull limits
    matchPullsPerDay: null, // Unlimited match pulls
    matchPullCountsTowardScenarios: false,
    maxTotalBoards: null, // Unlimited boards

    // Feature access
    canSeeCopyFromPickMode: true,
    canSeePracticeMatchup: true,
    canDuplicate: true,
    canSeeSorting: true,
    canSeeDuplicateToggle: true,
    canSeeHighlightsToggle: true,
    canSeeShareDraft: true,
    canSeeSeeMatchups: true,
    canSeeCopyToPickMode: true,
    canSeeViewRecords: true, // Show "View Records" button (Firebase-saved drafts)

    // Label
    label: 'Premium',
    color: '#ffa726'
  }
};

/**
 * UserTierService - Static methods for tier management
 */
class UserTierService {
  /**
   * Get tier limits for a user
   * @param {Object} user - User object with tier property
   * @returns {Object} - Tier limits object
   */
  static getTierLimits(user) {
    // Some games don't enforce tiers at all (mlbbdraftplan gave everyone
    // full access); preserved via the per-game flag.
    if (game.features.everyonePremium) {
      return TIER_LIMITS[TIERS.PREMIUM];
    }

    // Embed view (used by ygbuff Match History / Draft Layout overlay) bypasses
    // tier limits — access is already gated by the parent app's auth.
    if (typeof window !== 'undefined' && window.location && window.location.pathname.startsWith('/embed')) {
      return TIER_LIMITS[TIERS.PREMIUM];
    }

    if (!user || !user.tier) {
      return TIER_LIMITS[TIERS.FREE]; // Default to free tier
    }

    return TIER_LIMITS[user.tier] || TIER_LIMITS[TIERS.FREE];
  }

  /**
   * Check if user can add more scenarios
   * @param {Object} user - User object
   * @param {number} currentScenarioCount - Current number of scenarios in pick mode
   * @param {number} matchPullCount - Number of scenarios from match pulls
   * @returns {Object} - { canAdd: boolean, reason: string }
   */
  static canAddScenario(user, currentScenarioCount, matchPullCount = 0) {
    const limits = this.getTierLimits(user);

    // Premium users have no limits
    if (limits.maxScenariosPerPickMode === null) {
      return { canAdd: true, reason: null };
    }

    // Check scenario limit per pick mode
    if (currentScenarioCount >= limits.maxScenariosPerPickMode) {
      return {
        canAdd: false,
        reason: `Free tier limited to ${limits.maxScenariosPerPickMode} scenarios per pick mode. Upgrade to Premium for unlimited scenarios.`
      };
    }

    return { canAdd: true, reason: null };
  }

  /**
   * Check if user can pull match data
   * @param {Object} user - User object
   * @param {number} pullsToday - Number of match pulls made today
   * @returns {Object} - { canPull: boolean, reason: string, remaining: number }
   */
  static canPullMatchData(user, pullsToday = 0) {
    const limits = this.getTierLimits(user);

    // Premium users have no limits
    if (limits.matchPullsPerDay === null) {
      return { canPull: true, reason: null, remaining: null };
    }

    // Check daily limit
    if (pullsToday >= limits.matchPullsPerDay) {
      return {
        canPull: false,
        reason: `Free tier limited to ${limits.matchPullsPerDay} match pulls per day. Upgrade to Premium for unlimited pulls.`,
        remaining: 0
      };
    }

    return {
      canPull: true,
      reason: null,
      remaining: limits.matchPullsPerDay - pullsToday
    };
  }

  /**
   * Check if user has access to a feature
   * @param {Object} user - User object
   * @param {string} feature - Feature name (e.g., 'canSeeCopyFromPickMode')
   * @returns {boolean} - True if user has access
   */
  static hasFeatureAccess(user, feature) {
    const limits = this.getTierLimits(user);
    return limits[feature] === true;
  }

  /**
   * Get total board limit (scenarios + match pulls)
   * @param {Object} user - User object
   * @returns {number|null} - Max boards or null for unlimited
   */
  static getMaxTotalBoards(user) {
    const limits = this.getTierLimits(user);
    return limits.maxTotalBoards;
  }

  /**
   * Check if user can create more boards (total limit check)
   * @param {Object} user - User object
   * @param {number} totalBoards - Total current boards (scenarios + match pulls)
   * @returns {Object} - { canCreate: boolean, reason: string }
   */
  static canCreateBoard(user, totalBoards) {
    const maxBoards = this.getMaxTotalBoards(user);

    if (maxBoards === null) {
      return { canCreate: true, reason: null };
    }

    if (totalBoards >= maxBoards) {
      return {
        canCreate: false,
        reason: `Free tier limited to ${maxBoards} total boards. Upgrade to Premium for unlimited boards.`
      };
    }

    return { canCreate: true, reason: null };
  }

  /**
   * Get tier badge display info
   * @param {Object} user - User object
   * @returns {Object} - { label: string, color: string }
   */
  static getTierBadge(user) {
    const limits = this.getTierLimits(user);
    return {
      label: limits.label,
      color: limits.color
    };
  }

  /**
   * Check if user is admin
   * @param {Object} user - User object
   * @returns {boolean} - True if user is admin
   */
  static isAdmin(user) {
    return user && user.isAdmin === true;
  }

  /**
   * Get upgrade message for a specific limit
   * @param {string} limitType - Type of limit ('scenarios', 'matchPulls', etc.)
   * @returns {string} - Upgrade message
   */
  static getUpgradeMessage(limitType) {
    const messages = {
      scenarios: 'Upgrade to Premium for unlimited scenarios and advanced features!',
      matchPulls: 'Upgrade to Premium for unlimited match pulls!',
      copyFrom: 'Upgrade to Premium to copy scenarios between pick modes!',
      practiceMatchup: 'Upgrade to Premium to access Practice Match-up feature!',
      duplicate: 'Upgrade to Premium to duplicate scenarios!',
      sorting: 'Upgrade to Premium to reorder scenarios!'
    };

    return messages[limitType] || 'Upgrade to Premium for full access!';
  }
}

export default UserTierService;
